import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authService, eventService } from '../services/api';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';

const InvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [eventId, setEventId] = useState(null);

  useEffect(() => {
    const fetchInvitationDetails = async () => {
      try {
        const data = await eventService.getInvitationDetails(token);
        setInvitation(data);
        const currentEventId = data.event._id;
        setEventId(currentEventId); // Store event ID separately

        // If user is already authenticated
        if (authService.isAuthenticated()) {
          const currentUser = authService.getCurrentUser();
          
          // Check if the email matches
          if (currentUser.email === data.email) {
            // Auto-accept the invitation with the current event ID
            setAccepting(true);
            await acceptInvitationWithEventId(currentEventId);
          } else {
            setError(`You are logged in as ${currentUser.email}, but this invitation is for ${data.email}. Please log out and log in with the correct account.`);
          }
        }
        
      } catch (err) {
        console.error('Error fetching invitation:', err);
        if (err.message && err.message.includes('404')) {
          setError('This invitation link is invalid or has expired. Please contact the event organizer for a new invitation.');
        } else {
          setError(err.message || 'Failed to load invitation details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInvitationDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const acceptInvitationWithEventId = useCallback(async (targetEventId) => {
    try {
      await eventService.acceptInvitation(token);

      // Redirect to the event with the correct ID
      navigate(`/event/${targetEventId}/overview`, { replace: true });

    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err.message || 'Failed to accept invitation');
      setAccepting(false);
    }
  }, [token, navigate]);

  const handleSignUp = () => {
    // Pass the invitation email to the signup page
    navigate('/signup', { 
      state: { 
        email: invitation.email,
        returnTo: `/invite/${token}` // Return here after signup
      }
    });
  };

  const handleLogin = () => {
    // Pass the return URL to login page
    navigate('/login', { 
      state: { 
        returnTo: `/invite/${token}` // Return here after login
      }
    });
  };

  if (loading) {
    return (
      <div className="invite-page">
        <div className="invite-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invite-page">
        <div className="invite-container">
          <div className="error-container">
            <h2>Invitation Error</h2>
            <p>{error}</p>
            <button 
              className="primary-btn"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (accepting) {
    return (
      <div className="invite-page">
        <div className="invite-container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Accepting invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-page">
      <div className="invite-container">
        <div className="invite-header">
          <PersonAddIcon className="invite-icon" />
          <h1>Event Invitation</h1>
        </div>

        <div className="invite-details">
          <div className="invite-info">
            <div className="invite-field">
              <EventIcon className="field-icon" />
              <div className="field-content">
                <label>Event</label>
                <span>{invitation.event.name}</span>
              </div>
            </div>

            <div className="invite-field">
              <PersonIcon className="field-icon" />
              <div className="field-content">
                <label>Invited by</label>
                <span>{invitation.invitedBy.name}</span>
              </div>
            </div>

            <div className="invite-field">
              <PersonAddIcon className="field-icon" />
              <div className="field-content">
                <label>Role</label>
                <span className="role-badge">{invitation.role}</span>
              </div>
            </div>

            <div className="invite-field">
              <EventIcon className="field-icon" />
              <div className="field-content">
                <label>Event Date</label>
                <span>{new Date(invitation.event.startDate).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          <div className="invite-message">
            <p>You have been invited to collaborate on <strong>{invitation.event.name}</strong>.</p>
            <p>To accept this invitation, please sign up or log in with the email address: <strong>{invitation.email}</strong></p>
          </div>

          <div className="invite-actions">
            <button 
              className="primary-btn"
              onClick={handleSignUp}
            >
              Sign Up with {invitation.email}
            </button>
            
            <button 
              className="secondary-btn"
              onClick={handleLogin}
            >
              Already have an account? Log In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitePage;